import {inject, injectable} from 'inversify';

import type {CoreEditionApiModel} from '../../../../../api/types/core/models';
import type {CurrencyModel} from '../../../Currency/Currency.model';
import type {PaymentPlanModel} from '../PaymentPlan.model';

import {CurrencyFactory} from '../../../Currency/Currency.factory';
import {PaymentModel} from '../../../Payment/Payment.model';
import {PriceFactory} from '../../../Price/Price.factory';
import {PaymentPlanFactory} from '../PaymentPlan.factory';

@injectable()
export class PaymentPlanMapperJrpc {
  constructor(
    @inject(PaymentPlanFactory)
    private readonly factory: PaymentPlanFactory,
    @inject(CurrencyFactory)
    private readonly currencyFactory: CurrencyFactory,
    @inject(PriceFactory)
    private readonly priceFactory: PriceFactory,
  ) {}

  mapDataToModel(apiData: PaymentPlanMapperJrpc.ApiData): PaymentPlanModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(
    apiData: PaymentPlanMapperJrpc.ApiData,
  ): PaymentPlanModel.Props {
    const currency = this.currencyFactory.create(apiData.currency);

    return {
      currency,
      description: apiData.description,
      id: '',
      processing: this.processing(apiData),
      schedule: this.schedule(apiData.schedule, currency),
      total: this.priceFactory.create(apiData.total, currency),
    };
  }

  private processing(
    apiData: PaymentPlanMapperJrpc.ApiData,
  ): PaymentModel.Processing {
    switch (apiData.processing) {
      case 'external':
        return PaymentModel.Processing.EXTERNAL;
      case 'internal':
        return PaymentModel.Processing.INTERNAL;
    }
  }

  private schedule(
    apiData: PaymentPlanMapperJrpc.ApiData['schedule'],
    currency: CurrencyModel,
  ): PaymentPlanModel.ScheduleItem[] {
    return apiData.map(item => ({
      amount: this.priceFactory.create(item.amount, currency),
      day: item.day,
      uuid: item.uuid,
    }));
  }
}

export namespace PaymentPlanMapperJrpc {
  export type ApiData = NonNullable<CoreEditionApiModel['payment_plan']>;
}
